import React, { useState } from 'react';
import { Target, Eye, Award, Users, Globe, TrendingUp, Heart, Zap } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Toaster } from '../components/ui/toaster';
import GetInvolvedModal from '../components/GetInvolvedModal';
import ContactModal from '../components/ContactModal';

const About = () => {
  const [showGetInvolved, setShowGetInvolved] = useState(false);
  const [showContact, setShowContact] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <Toaster />
      <GetInvolvedModal isOpen={showGetInvolved} onClose={() => setShowGetInvolved(false)} />
      <ContactModal isOpen={showContact} onClose={() => setShowContact(false)} />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-black via-gray-900 to-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent">
            About the Kingdom of Kush Youth Parliament
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Empowering young African leaders to shape the future of the continent through democratic participation, innovative policy-making, and collaborative action.
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <Card className="border-2 border-yellow-400">
              <CardHeader>
                <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center mb-4">
                  <Target className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-3xl">Our Mission</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">
                  To provide a platform for African youth to actively participate in governance, develop progressive policies, and drive sustainable development across the continent. We aim to amplify youth voices in decision-making processes and foster a new generation of responsible, visionary leaders committed to Africa's prosperity.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-red-600">
              <CardHeader>
                <div className="w-16 h-16 bg-gradient-to-br from-red-600 to-red-700 rounded-full flex items-center justify-center mb-4">
                  <Eye className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-3xl">Our Vision</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">
                  A united, prosperous Africa where youth are at the forefront of shaping policies that address education, healthcare, economic development, environmental sustainability, and social justice. We envision a continent where every young person has the opportunity to contribute to and benefit from Africa's transformation.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Our Core Values</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Award,
                title: 'Excellence',
                description: 'Striving for the highest standards in policy-making and governance',
                color: 'from-yellow-400 to-yellow-600'
              },
              {
                icon: Users,
                title: 'Unity',
                description: 'Bringing together diverse voices across 54 African nations',
                color: 'from-red-600 to-red-700'
              },
              {
                icon: Heart,
                title: 'Integrity',
                description: 'Upholding honesty, transparency, and ethical leadership',
                color: 'from-black to-gray-800'
              },
              {
                icon: Zap,
                title: 'Innovation',
                description: 'Embracing creative solutions to continental challenges',
                color: 'from-yellow-600 to-yellow-800'
              },
            ].map((value, index) => (
              <div key={index} className="text-center group">
                <div className={`w-20 h-20 bg-gradient-to-br ${value.color} rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform`}>
                  <value.icon className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{value.title}</h3>
                <p className="text-gray-600 text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* History */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Our Journey</h2>
          <div className="space-y-8">
            {[
              {
                year: '2020',
                title: 'Foundation',
                description: 'Kingdom of Kush Youth Parliament established with founding members from 15 countries, laying the groundwork for pan-African youth governance.'
              },
              {
                year: '2021',
                title: 'Continental Expansion',
                description: 'Expanded to all 54 African nations, establishing committees and democratic structures for youth participation.'
              },
              {
                year: '2023',
                title: 'First Major Legislation',
                description: 'Passed the African Youth Charter, establishing rights and frameworks for youth development across the continent.'
              },
              {
                year: '2025',
                title: 'Digital Transformation',
                description: 'Launched comprehensive digital platforms and initiatives, reaching over 10 million young Africans through technology programs.'
              },
            ].map((milestone, index) => (
              <div key={index} className="flex gap-8">
                <div className="flex-shrink-0">
                  <div className="w-24 h-24 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center">
                    <span className="text-2xl font-bold text-black">{milestone.year}</span>
                  </div>
                </div>
                <div className="flex-1 pt-4">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-2">{milestone.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Achievements */}
      <section className="py-16 bg-gradient-to-r from-red-600 to-red-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">Key Achievements</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Globe,
                value: '120+',
                label: 'Representatives',
                description: 'From across Africa'
              },
              {
                icon: Award,
                value: '23',
                label: 'Bills Passed',
                description: 'In 2025 alone'
              },
              {
                icon: TrendingUp,
                value: '10M+',
                label: 'Youth Impacted',
                description: 'Through our programs'
              },
            ].map((achievement, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <achievement.icon className="h-8 w-8" />
                </div>
                <div className="text-4xl font-bold mb-2">{achievement.value}</div>
                <div className="text-xl font-semibold mb-1">{achievement.label}</div>
                <div className="text-white/80 text-sm">{achievement.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Get Involved */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Get Involved</h2>
          <p className="text-xl text-gray-700 mb-8">
            Join us in shaping Africa's future. Whether you're interested in policy-making, community engagement, or contributing your skills, there's a place for you in the Kingdom of Kush Youth Parliament.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              size="lg" 
              onClick={() => setShowGetInvolved(true)}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:from-cyan-600 hover:to-blue-700 font-semibold shadow-lg"
            >
              Apply for Membership
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              onClick={() => setShowGetInvolved(true)}
              className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white"
            >
              Volunteer with Us
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              onClick={() => setShowContact(true)}
              className="border-2 border-orange-600 text-orange-600 hover:bg-orange-600 hover:text-white"
            >
              Support Our Work
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;